import React, { useState } from 'react';
import { showToast } from '@trellixio/roaster-coffee';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Urls } from 'data';
import { validateEmail } from 'helpers';
import { useAPI } from 'hooks';
import Locales from 'locales';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, ArrowLeft, Info } from 'lucide-react';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [displayError, setDisplayError] = useState(false);
  const navigate = useNavigate();
  const { api } = useAPI();

  const queryClient = useQueryClient();
  const isMutating = queryClient.isMutating() > 0;

  const { mutate: checkEmail } = useMutation({
    mutationFn: () => api.post('auth/user_token/forgot_password/', { email }),
    onSuccess: () => {
      setDisplayError(false);
      showToast({
        message: Locales.formatString(Locales.auth.forgot_pwd.alert, { email }),
        level: 'success',
      });
      navigate(Urls.front.login);
    },
  });

  const handleForgotPassword = (event) => {
    event.preventDefault();
    const emptyEmailField = email.trim() === '';
    const validEmail = validateEmail(email);
    if (!emptyEmailField && validEmail) {
      setDisplayError(false);
      checkEmail();
    } else {
      setDisplayError(true);
    }
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-hajj-muted via-white to-green-50 flex items-center justify-center p-4 md:pl-[440px]">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-hajj-primary/5 rounded-full blur-3xl animate-pulse" />
        <div
          className="absolute bottom-20 right-10 w-96 h-96 bg-green-200/20 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: '1s' }}
        />
      </div>

      <div className="relative w-full max-w-md animate-scale-in">
        <div className="space-y-8">
          <div className="text-center space-y-3">
            <h1 className="text-4xl font-bold text-gray-900 tracking-tight">{Locales.auth.forgot_pwd.title}</h1>
            <p className="text-base text-gray-600 max-w-sm mx-auto">
              Entrez votre email pour réinitialiser votre mot de passe
            </p>
          </div>

          {displayError && (
            <Alert variant="destructive" className="animate-slide-up">
              <AlertDescription>{Locales.auth.email_error_message}</AlertDescription>
            </Alert>
          )}

          <Alert className="border-blue-200 bg-blue-50 text-blue-900 animate-slide-up">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription>{Locales.auth.forgot_pwd.description}</AlertDescription>
          </Alert>

          <form onSubmit={handleForgotPassword} className="space-y-6">
            <div className="space-y-3">
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                {Locales.base.field.email}
              </Label>
              <Input
                id="email"
                type="email"
                placeholder={Locales.base.field.placeholder.email}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setDisplayError(false);
                }}
                className="h-14 text-lg bg-white/80 backdrop-blur-sm border-2 border-gray-200 focus:border-hajj-primary focus:ring-4 focus:ring-hajj-primary/10 transition-all duration-200 rounded-xl"
                autoFocus
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 text-base font-medium bg-hajj-primary hover:bg-hajj-secondary text-white rounded-xl shadow-lg shadow-hajj-primary/20 hover:shadow-xl hover:shadow-hajj-primary/30 transition-all duration-200"
              disabled={isMutating}
            >
              {isMutating ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                Locales.auth.forgot_pwd.action
              )}
            </Button>

            <div className="text-center pt-2">
              <Link
                to={Urls.front.login}
                className="inline-flex items-center text-sm text-hajj-primary hover:text-hajj-secondary font-medium transition-colors"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                {Locales.auth.connect}
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
